import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { sortBy } from 'lodash';
import { titleize } from 'underscore.string';

import CustomerSearch from 'components/site/customer-search';
import CargomaticBadge from 'components/v2/Badge';
import CargomaticInput from 'components/v2/input';
import CargomaticAlert from 'components/ui/Alert';
import Spinner from 'components/ui/Spinner';
import SplitIcon from 'components/v2/icons/SplitIcon';
import ShipmentWebAPIUtils from 'utils/ShipmentWebAPIUtils';
import { getShipmentDetailUrl } from 'utils/formatters/shipments';
import { useTranslation } from 'react-i18next';
import PanelSection from 'components/v2/PanelSection';

const SplitShipment = (props) => {
  const { shipmentId, originJob, newJob, onCancel } = props;

  const [carrierToAssign, setCarrierToAssign] = useState(null);
  const [removeCarrierCost, setRemoveCarrierCost] = useState(false);
  const [removeShipperCost, setRemoveShipperCost] = useState(false);
  const [includeCarrierCostOnNew, setIncludeCarrierCostOnNew] = useState(false);
  const [includeShipperCostOnNew, setincludeShipperCostOnNew] = useState(false);
  const [shipmentCreated, setShipmentCreated] = useState(null);
  const [isSplitting, setIsSplitting] = useState(false);
  const { t } = useTranslation();
  useEffect(() => {}, [originJob, newJob]);
  const INPUT_MESSAGES = {
    REMOVE_CARRIER: t('REMOVE_CARRIER_MESSAGE'),
    REMOVE_SHIPPER: t('REMOVE_SHIPPER_MESSAGE'),
    INCLUDE_CARRIER_ON_NEW: t('INCLUDE_CARRIER_ON_NEW_MESSAGE'),
    INCLUDE_SHIPPER_ON_NEW: t('INCLUDE_SHIPPER_ON_NEW_MESSAGE'),
  };

  const STOP_INTENTS = {
    PICKUP: 'pickup',
    DELIVER: 'deliver',
  };

  const resetFields = () => {
    setCarrierToAssign(null);
    setRemoveCarrierCost(false);
    setRemoveShipperCost(false);
    setIncludeCarrierCostOnNew(false);
    setincludeShipperCostOnNew(false);
    setIsSplitting(false);
  };

  const splitShipment = async () => {
    const payload = {
      stops: newJob.stops.map((stop) => stop._id.toString()),
      carrier: carrierToAssign ? carrierToAssign._id : null,
      actions: {
        remove_carrier_cost: removeCarrierCost,
        remove_shipper_cost: removeShipperCost,
        include_carrier_cost_on_new: includeCarrierCostOnNew,
        include_shipper_cost_on_new: includeShipperCostOnNew,
      },
    };
    setIsSplitting(true);
    const response = await ShipmentWebAPIUtils.splitShipment(
      shipmentId,
      payload,
    ).catch((e) => {
      console.log(e);
    });
    if (response.success) {
      const { shipment_id, _id } = response.job;
      setShipmentCreated({ shipmentId: shipment_id, _id });
      setTimeout(onCancel, 8000);
      setTimeout(() => setShipmentCreated(null), 8000);
    }
    resetFields();
  };

  const renderSuccess = () => {
    return (
      <CargomaticAlert
        type="success"
        canDelete
        onDelete={() => setShipmentCreated(null)}
      >
        {t('SPLIT_SHIPMENT_SUCCESS')}
        <Link
          id="new-shipment-link"
          to={getShipmentDetailUrl(shipmentCreated._id)}
          target="_blank"
        >
          {shipmentCreated.shipmentId}
        </Link>{' '}
      </CargomaticAlert>
    );
  };

  const getCheckbox = (message, checked, onChange) => {
    return (
      <div className="flex cursor-pointer" onClick={onChange}>
        <CargomaticInput
          type="checkbox"
          onChange={onChange}
          checked={checked}
        />
        <p className="pl-2">{message}</p>
      </div>
    );
  };

  const getAddressInfo = (stop) => {
    if (!stop || !stop.location) {
      return null;
    }
    return (
      <div className="flex flex-col">
        <p>{stop.location[0].name}</p>
        <p>{stop.location[0].display_address}</p>
      </div>
    );
  };

  const getStop = (stop) => {
    const { action, off_site, empty } = stop;

    const getAction = () => {
      if (action === STOP_INTENTS.PICKUP) {
        if (empty) {
          return t('PICKUP_EMPTY');
        }
        return t('PICKUP');
      }
      if (empty) {
        return t('DELIVERY_EMPTY');
      }
      return t('DELIVERY');
    };

    return (
      <div id={stop._id} className="w-5/6 my-4">
        <div className="flex justify-between">
          <p>{titleize(getAction())}</p>
          <div className="w-3/4 flex flex-col">
            {getAddressInfo(stop)}
            {off_site ? (
              <CargomaticBadge
                className="my-2"
                color="blue"
                text={t('OFFSITE_STOP')}
              />
            ) : null}
          </div>
        </div>
      </div>
    );
  };

  const getShipment = ({ stops, carrier }, allowAssignCarrier, count = 1) => {
    const getCarrierField = () => {
      if (carrier) {
        return <p>{carrier.name}</p>;
      }
      if (!allowAssignCarrier) {
        return null;
      }
      return (
        <CustomerSearch
          onSelect={(_item, data) => setCarrierToAssign(data)}
          placeholder={t('ASSIGN_A_CARRIER')}
          type="carrier"
          authorizedOnly
        />
      );
    };

    const sortedStops = sortBy(stops, (stop) => stop.sequence);

    return (
      <div>
        <p className="font-semibold my-2">
          {t('SHIPMENT_COUNT', { shipmentCount: count })}
        </p>
        <div className="w-5/6 h-12 flex my-2">
          <p className="w-1/4">{titleize(t('CARRIER'))}</p>
          <div className="flex">
            {getCarrierField()}
            {allowAssignCarrier && carrierToAssign && (
              <CargomaticBadge
                className="pl-2 my-auto"
                color="blue"
                text={carrierToAssign.name}
              />
            )}
          </div>
        </div>
        {sortedStops.map((stop) => getStop(stop))}
      </div>
    );
  };

  return (
    <PanelSection className="flex flex-col">
      <h4 className="flex pb-2">{t('VERIFY_SPLIT')}</h4>
      <div className="flex">
        <div className="w-2/5">
          <div className="pl-8 mx-auto">
            {getShipment(originJob, false)}
            <div className="my-8">
              {getCheckbox(
                INPUT_MESSAGES.REMOVE_CARRIER,
                removeCarrierCost,
                () => setRemoveCarrierCost(!removeCarrierCost),
              )}
              {getCheckbox(
                INPUT_MESSAGES.REMOVE_SHIPPER,
                removeShipperCost,
                () => setRemoveShipperCost(!removeShipperCost),
              )}
            </div>
          </div>
        </div>
        <div className="1/5 my-auto mx-auto pr-16">
          <div className="p-4 rounded-full bg-gray-300">
            <SplitIcon color="#38495c" className="w-8 h-8" />
          </div>
        </div>
        <div className="w-2/5">
          <div className="pl-8 mx-auto">
            {getShipment(newJob, true, 2)}
            <div className="my-8">
              {getCheckbox(
                INPUT_MESSAGES.INCLUDE_CARRIER_ON_NEW,
                includeCarrierCostOnNew,
                () => setIncludeCarrierCostOnNew(!includeCarrierCostOnNew),
              )}
              {getCheckbox(
                INPUT_MESSAGES.INCLUDE_SHIPPER_ON_NEW,
                includeShipperCostOnNew,
                () => setincludeShipperCostOnNew(!includeShipperCostOnNew),
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="py-4">
        <div className="flex flex-row justify-end">
          <button className="button button-red mr-2" onClick={onCancel}>
            {titleize(t('CANCEL'))}
          </button>
          <button
            className="button button-green"
            disabled={isSplitting || !!shipmentCreated}
            onClick={splitShipment}
          >
            {isSplitting ? <Spinner message={t('SPLITTING')} /> : t('CONTINUE')}
          </button>
        </div>
        <div className="flex flex-row justify-end py-4">
          {shipmentCreated && renderSuccess()}
        </div>
      </div>
    </PanelSection>
  );
};

export default SplitShipment;
